import { MenuOutlined } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  IconButton,
  Link,
  List,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import LinkButton from "app/frontend/components/LinkButton";
import { ROUTES } from "app/frontend/constants/routes";
import { useCallback, useEffect, useRef, useState } from "react";

import { toId } from "@frontend/utils/inputs";
import CloseIcon from "app/frontend/assets/icons/icn-close.svg?react";
import Coned from "app/frontend/assets/images/coned.svg?react";
import HeaderMenuDesktop from "./header/HeaderMenuDesktop";
import HeaderMenuMobile from "./header/HeaderMenuMobile";
import { EXTERNAL_PAGES, PAGES } from "./header/constants";
import { useDesktopButtonSx } from "./header/useDesktopButtonSx";
import { noop } from "@frontend/utils";

const Header = () => {
  const theme = useTheme();

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElNav(event.currentTarget);
    },
    [],
  );

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
  }, []);

  // Issue 231 - Remove "Home" from header. This could change so we'll just hide it for now.
  // const getButtonDesktopPageSX = useDesktopButtonSx();

  const [topBarOffset, setTopBarOffset] = useState(0);
  const topBarRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    setTopBarOffset(topBarRef.current?.offsetHeight || 0);
  }, [topBarRef.current?.offsetHeight]);

  const getButtonDesktopPageSX = useDesktopButtonSx();

  return (
    <>
      <AppBar
        ref={topBarRef}
        id="app-header-black"
        position="static"
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "row",
          justifyContent: "flex-end",
          backgroundColor: "grey.900",
          boxShadow: "none",
          minWidth: "100%",
          top: 0,
          zIndex: 999,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexShrink: "0",
            alignSelf: "stretch",
            gap: "4px",
            marginRight: { xs: 0, md: 10 },
          }}
        >
          {
            // NOTE: These are hidden as of 10/1/2025 because they are not implemented in this project
            // Keeping them here in case they are needed in the future.
            EXTERNAL_PAGES.map((page) => (
              <Button
                key={`external-${toId(page.title)}`}
                component={Link}
                startIcon={<page.icon />}
                href={page.url}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: "common.white",
                  fontWeight: "bold",
                  textTransform: "none",
                  visibility: "hidden", // removed from Figma
                }}
              >
                {page.title}
              </Button>
            ))
          }
        </Box>
      </AppBar>
      <AppBar
        id="app-header"
        position="static"
        sx={{
          backgroundColor: "white",
          boxShadow: "none",
          borderBottom: `1px solid ${theme.palette.secondary.dark}`,
          minWidth: "100%",
          top: topBarOffset,
          zIndex: 999,
          overflow: "hidden",
        }}
      >
        <Container disableGutters sx={{ mx: 0, minWidth: "100%" }}>
          <Toolbar disableGutters sx={{ borderTop: "1px solid black" }}>
            <Container sx={{ display: "flex", alignSelf: "center" }}>
              <LinkButton href={ROUTES.ROOT}>
                <Coned title="The Con Edison Logo" fill={"#0099d8"} />
              </LinkButton>
            </Container>

            <Box
              sx={{
                flexGrow: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                display: { xs: "none", sm: "flex" },
              }}
            >
              {PAGES.map((page, i) => (
                <HeaderMenuDesktop
                  key={`page-${page.routes.main}-${i}`}
                  page={page}
                />
              ))
                // Issue 231 - Remove "Home" from header. This could change so we'll just hide it for now.
                .concat([
                  <Button
                    key={"empty-box"}
                    onClick={noop}
                    sx={getButtonDesktopPageSX(null, theme)}
                    aria-hidden={true}
                    disabled
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  >
                    <Typography
                      component={"div"}
                      variant="caption"
                      sx={{
                        visibility: "hidden",
                        fontWeight: 700,
                      }}
                    >
                      {PAGES.last().name({})}
                    </Typography>
                  </Button>,
                ])}
            </Box>

            {/* MOBILE */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "flex", sm: "none" },
              }}
            >
              <Button
                title="Menu"
                onClick={handleOpenNavMenu}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  borderRadius: 0,
                  mr: 0.25,
                  py: 2,
                }}
              >
                <MenuOutlined titleAccess="Open or Close the Menu" width={22} />
              </Button>
              <Dialog
                id="menu-appbar"
                fullScreen
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "initial", sm: "none" },
                }}
              >
                <Toolbar
                  id="toolbar-mobile-header"
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    maxWidth: "100vw",
                  }}
                >
                  <Container
                    disableGutters
                    sx={{ display: { xs: "flex", sm: "none" } }}
                  >
                    <LinkButton href={ROUTES.ROOT}>
                      <Coned title="The Con Edison Logo" fill={"#0099d8"} />
                    </LinkButton>
                  </Container>
                  <IconButton
                    title="Close"
                    onClick={handleCloseNavMenu}
                    sx={{ p: 0 }}
                  >
                    <CloseIcon
                      title="Close"
                      style={{
                        fill: theme.palette.primary.main,
                        fontSize: "1em",
                      }}
                    />
                  </IconButton>
                </Toolbar>
                <Box>
                  <List>
                    <HeaderMenuMobile />
                  </List>
                </Box>
              </Dialog>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </>
  );
};

export default Header;
